import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';

export interface UseCaseTagPillsAtomProps {
  tags: string[];
  maxVisible?: number;
  onTagClick?: (tag: string) => void;
  size?: 'small' | 'normal';
  className?: string;
}

const UseCaseTagPillsAtom: React.FC<UseCaseTagPillsAtomProps> = ({
  tags,
  maxVisible = 3,
  onTagClick,
  size = 'normal',
  className = '',
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpanded((prev) => !prev);
    },
    [],
  );

  if (!tags || tags.length === 0) return null;

  const visibleTags = expanded ? tags : tags.slice(0, maxVisible);
  const hiddenCount = tags.length - maxVisible;

  const classes = [
    'marketplace-tag-pills',
    `marketplace-tag-pills--${size}`,
    onTagClick ? 'marketplace-tag-pills--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const formatTagLabel = (tag: string): string => {
    return tag
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={classes}>
      {visibleTags.map((tag) => (
        <span
          key={tag}
          className="marketplace-tag-pills__pill"
          role={onTagClick ? 'button' : undefined}
          tabIndex={onTagClick ? 0 : undefined}
          onClick={
            onTagClick
              ? (e) => {
                  e.stopPropagation();
                  onTagClick(tag);
                }
              : undefined
          }
          onKeyDown={
            onTagClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onTagClick(tag);
                  }
                }
              : undefined
          }
        >
          {formatTagLabel(tag)}
        </span>
      ))}
      {hiddenCount > 0 && !expanded && (
        <button
          type="button"
          className="marketplace-tag-pills__more"
          onClick={handleToggle}
        >
          {t('marketplace.useCases.showMore', { count: hiddenCount })}
        </button>
      )}
      {expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="marketplace-tag-pills__more"
          onClick={handleToggle}
        >
          {t('marketplace.useCases.showLess')}
        </button>
      )}
    </div>
  );
};

export default UseCaseTagPillsAtom;
